import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import {
  DraftChangeKind,
  IDraftRecommendation,
  IDraftRunDetail,
} from "../../../store/queries/contractModelDrafts";
import { componentTypeLabel } from "../utils";
import { stepForRecommendation, WIZARD_STEPS } from "./steps";

const stepTitle = (rec: IDraftRecommendation): string =>
  WIZARD_STEPS.find((s) => s.id === stepForRecommendation(rec))?.title ?? "";

const fmt = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const modelingOf = (rec: IDraftRecommendation): Record<string, unknown> =>
  (rec.proposedModeling ?? {}) as Record<string, unknown>;

const originalOf = (rec: IDraftRecommendation): Record<string, unknown> =>
  (rec.originalModeling ?? {}) as Record<string, unknown>;

/**
 * A changed recommendation. Always names WHICH STEP it belongs to and WHY it moved.
 *
 * Cause attribution is the anti-ambush mechanism. A regeneration is global, so an instruction she gave
 * on the Inpatient step can legitimately change something she already confirmed on Contract Terms. That
 * is not a bug — it is the tool catching an inconsistency a chat LLM would have let her ship. But she
 * must never have to WONDER why something moved, or the whole feature reads as the ground shifting
 * under her.
 */
function ChangeCard({
  rec,
  onOpen,
}: {
  rec: IDraftRecommendation;
  onOpen: (recId: string) => void;
}) {
  const changed = rec.mergeDetails?.changedFields ?? [];
  const conflicts = rec.mergeDetails?.conflicts ?? [];
  const modeling = modelingOf(rec);
  const original = originalOf(rec);

  return (
    <div className="surface-card border-round p-3 mb-2 border-1 surface-border">
      <div className="flex align-items-center flex-wrap gap-2 mb-1">
        <span className="font-semibold">
          {componentTypeLabel(rec.componentType)}
        </span>
        <Tag className="white-space-nowrap" value={stepTitle(rec)} />
        {rec.implementedAt ? (
          <Tag
            severity="danger"
            icon="pi pi-exclamation-triangle"
            value="You already built this"
          />
        ) : null}
      </div>

      {rec.changeReason ? (
        <p className="mt-0 mb-2 text-sm text-color-secondary">
          {rec.changeReason}
        </p>
      ) : null}

      {conflicts.length > 0 && (
        <div className="mb-2">
          {conflicts.map((c) => (
            <div key={c.path} className="grid mt-1 text-sm">
              <div className="col-12 font-medium">{c.path}</div>
              <div className="col-6">
                <div className="text-color-secondary text-xs">You set</div>
                <div className="font-semibold">{fmt(c.ours)}</div>
              </div>
              <div className="col-6">
                <div className="text-color-secondary text-xs">
                  The agent now says
                </div>
                <div className="font-semibold">{fmt(c.theirs)}</div>
              </div>
            </div>
          ))}
          <small className="text-color-secondary">
            We&rsquo;ve kept your value. Open it to decide.
          </small>
        </div>
      )}

      {conflicts.length === 0 && changed.length > 0 && (
        <div className="text-sm">
          {changed.map((field) => (
            <div key={field} className="flex gap-2 align-items-baseline">
              <span className="font-medium">{field}:</span>
              <span className="text-color-secondary line-through">
                {fmt(original[field] ?? "—")}
              </span>
              <i className="pi pi-arrow-right text-xs" />
              <span className="font-semibold">{fmt(modeling[field])}</span>
            </div>
          ))}
        </div>
      )}

      {rec.changeKind === "superseded" && (
        <div className="text-sm text-color-secondary">
          The agent no longer proposes this.
          {rec.implementedAt
            ? " You have already built it — it may need to be removed."
            : ""}
        </div>
      )}

      <Button
        label="Open"
        icon="pi pi-arrow-right"
        iconPos="right"
        className="p-button-text p-button-sm p-0 mt-2"
        onClick={() => onOpen(rec.id)}
      />
    </div>
  );
}

function Section({
  title,
  blurb,
  severity,
  recs,
  onOpen,
}: {
  title: string;
  blurb?: string;
  severity?: "danger" | "warning";
  recs: IDraftRecommendation[];
  onOpen: (recId: string) => void;
}) {
  if (recs.length === 0) return null;
  const SEVERITY_COLOR: Record<string, string> = {
    danger: "text-red-600",
    warning: "text-orange-600",
  };
  const color = severity ? SEVERITY_COLOR[severity] : "";
  return (
    <div className="mb-4">
      <h4 className={`mt-0 mb-1 ${color}`}>
        {severity ? <i className="pi pi-exclamation-triangle mr-2" /> : null}
        {title} ({recs.length})
      </h4>
      {blurb ? (
        <p className="mt-0 mb-2 text-sm text-color-secondary">{blurb}</p>
      ) : null}
      {recs.map((rec) => (
        <ChangeCard key={rec.id} rec={rec} onOpen={onOpen} />
      ))}
    </div>
  );
}

interface IProps {
  run: IDraftRunDetail;
  onOpen: (recId: string) => void;
}

/**
 * Everything that moved in the last regeneration, in one place, ordered by how much it should worry
 * her — rather than scattered as badges across seven wizard steps she would have to go hunting through.
 */
function ChangesScreen({ run, onOpen }: IProps) {
  const summary = run.mergeSummary;
  const of = (kind: DraftChangeKind) =>
    run.recommendations.filter((r) => r.changeKind === kind);

  // She BUILT this in Payer Rules, and it has since changed or been withdrawn. She built the wrong
  // thing, and it may be live money. This is the highest-severity state in the entire tool and it goes
  // first, on its own, in red.
  const alreadyBuilt = run.recommendations.filter(
    (r) =>
      r.implementedAt &&
      (r.changeKind === "modified" ||
        r.changeKind === "conflicted" ||
        r.changeKind === "superseded")
  );
  const builtIds = new Set(alreadyBuilt.map((r) => r.id));
  const without = (recs: IDraftRecommendation[]) =>
    recs.filter((r) => !builtIds.has(r.id));

  if (!summary) {
    return (
      <p className="text-500">
        This run hasn&rsquo;t been regenerated yet, so there&rsquo;s nothing to
        compare against. Add an instruction or a document, then regenerate.
      </p>
    );
  }

  const nothingMoved =
    summary.modified === 0 &&
    summary.conflicted === 0 &&
    summary.new === 0 &&
    summary.superseded === 0;

  return (
    <div>
      <h3 className="mt-0 mb-1">What changed in v{run.versionNo}</h3>
      <p className="mt-0 mb-3 text-500">
        Only what actually moved. Everything else kept the verdict you already
        gave it.
      </p>

      {nothingMoved ? (
        <div className="surface-100 border-round p-3">
          <div className="font-semibold mb-1">Nothing changed.</div>
          <small className="text-color-secondary">
            The agent re-read everything and reached the same conclusions. Your
            review still stands.
          </small>
        </div>
      ) : (
        <>
          <Section
            title="You already built these — and they have changed"
            blurb="You ticked these off as built in Payer Rules, but the recommendation has since moved. What you built no longer matches. Check them first."
            severity="danger"
            recs={alreadyBuilt}
            onOpen={onOpen}
          />
          <Section
            title="The agent disagrees with a correction you made"
            blurb="You set a value; the agent now proposes a different one. We have kept yours — you decide."
            severity="warning"
            recs={without(of("conflicted"))}
            onOpen={onOpen}
          />
          <Section
            title="Changed"
            recs={without(of("modified"))}
            onOpen={onOpen}
          />
          <Section title="New" recs={without(of("new"))} onOpen={onOpen} />
          <Section
            title="Withdrawn"
            blurb="The agent no longer proposes these."
            recs={without(of("superseded"))}
            onOpen={onOpen}
          />
        </>
      )}

      {/*
        The most important line on the page. It is the direct answer to "did it just blow away my
        work?" — and it doubles as the health metric for the whole feature: if a narrow instruction
        comes back with most recommendations changed, identity matching or the field diff is broken,
        and she will lose trust in one session and never get it back. So it is shown, not buried in
        telemetry.
      */}
      <div className="surface-100 border-round p-3 mt-3">
        <span className="font-semibold">
          {summary.unchanged} of {summary.total} recommendations unchanged
        </span>
        <span className="text-color-secondary">
          {" "}
          — your confirmations were kept.
        </span>
      </div>
    </div>
  );
}

export default ChangesScreen;
